import type { WizardEvalRunResult } from '@/features/wizard-evals/api/wizard-evals-api';

const VALID_OUTCOMES: ReadonlySet<string> = new Set([
  'eligible',
  'ineligible',
  'unsure',
]);
const VALID_RATE_CLASSES: ReadonlySet<string> = new Set([
  'preferred',
  'standard',
  'tobacco',
  'non_tobacco',
  'not_available',
]);

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function asOutcome(value: unknown): UnderwritingOutcome {
  return typeof value === 'string' && VALID_OUTCOMES.has(value)
    ? (value as UnderwritingOutcome)
    : 'unsure';
}

function asRateClass(value: unknown): UnderwritingRateClass {
  return typeof value === 'string' && VALID_RATE_CLASSES.has(value)
    ? (value as UnderwritingRateClass)
    : 'not_available';
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asConfidence(value: unknown): FlagConfidence {
  return value === 'high' || value === 'medium' || value === 'low'
    ? (value as FlagConfidence)
    : 'medium';
}

function mapApplicants(
  value: unknown,
): StructuredUnderwritingApplicant[] | null {
  if (!Array.isArray(value)) return null;
  return value
    .map(entry => {
      const rec = asRecord(entry);
      if (!rec) return null;
      return {
        name: asString(rec.name),
        outcome: asOutcome(rec.outcome),
        rate_class: asRateClass(rec.rate_class),
        rationale: asString(rec.rationale),
      };
    })
    .filter((x): x is StructuredUnderwritingApplicant => x !== null);
}

function mapFlaggedItems(
  value: unknown,
): StructuredUnderwritingFlaggedItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(entry => {
      const rec = asRecord(entry);
      if (!rec) return null;
      return {
        description: asString(rec.description),
        source: asStringOrNull(rec.source),
        applicant: asStringOrNull(rec.applicant),
        confidence: asConfidence(rec.confidence),
        confidence_reasoning: asStringOrNull(rec.confidence_reasoning),
      };
    })
    .filter((x): x is StructuredUnderwritingFlaggedItem => x !== null);
}

function mapRecommendations(
  value: unknown,
): StructuredUnderwritingRecommendation[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(entry => {
      const rec = asRecord(entry);
      if (!rec) return null;
      return {
        description: asString(rec.description),
        applicant: asStringOrNull(rec.applicant),
      };
    })
    .filter((x): x is StructuredUnderwritingRecommendation => x !== null);
}

function mapVerification(value: unknown): UnderwritingVerification | null {
  const rec = asRecord(value);
  if (!rec) return null;
  const corrections = Array.isArray(rec.corrections)
    ? rec.corrections
        .map(c => {
          const cr = asRecord(c);
          if (!cr) return null;
          return {
            field: asString(cr.field),
            original_value: asString(cr.original_value),
            corrected_value: asString(cr.corrected_value),
            reason: asString(cr.reason),
          };
        })
        .filter((x): x is UnderwritingVerification['corrections'][number] => x !== null)
    : [];
  const missed_rules = Array.isArray(rec.missed_rules)
    ? rec.missed_rules
        .map(m => {
          const mr = asRecord(m);
          if (!mr) return null;
          return {
            rule: asString(mr.rule),
            impact: asString(mr.impact),
          };
        })
        .filter((x): x is UnderwritingVerification['missed_rules'][number] => x !== null)
    : [];
  return {
    confirmed: rec.confirmed === true,
    corrections,
    missed_rules,
  };
}

export interface RunStructuredView {
  structuredResult: StructuredUnderwritingResult | null;
  pipelineMetadata: UnderwritingPipelineMetadata | null;
}

export function mapRunResultToStructured(
  result: WizardEvalRunResult,
): RunStructuredView {
  const reviewer = asRecord(result.reviewerResult);
  const assessor = asRecord(result.assessorOutput);

  const finalResult = reviewer ? asRecord(reviewer.final_result) : null;
  const source = finalResult ?? assessor;

  let structuredResult: StructuredUnderwritingResult | null = null;
  if (source) {
    structuredResult = {
      outcome: asOutcome(source.outcome),
      rate_class: asRateClass(source.rate_class),
      rationale: asString(source.rationale),
      eligibility_overview: asString(source.eligibility_overview),
      applicants: mapApplicants(source.applicants),
      flagged_items: mapFlaggedItems(source.flagged_items),
      recommendations: mapRecommendations(source.recommendations),
    };
  }

  const verification = reviewer ? mapVerification(reviewer.verification) : null;
  const reviewStatus: UnderwritingPipelineMetadata['reviewStatus'] =
    result.reviewStatus === 'reviewed' ? 'reviewed' : 'unreviewed';

  const pipelineMetadata: UnderwritingPipelineMetadata | null = assessor
    ? {
        reviewStatus,
        verification,
        assessorOutput: assessor,
      }
    : null;

  return { structuredResult, pipelineMetadata };
}
